import type { $Fetch } from 'ofetch';
import type { AgendamentoParticipanteGateway } from '@/application/port/out/AgendamentoParticipanteGateway';
import type { DisciplineDocentesGateway } from '@/application/port/out/DisciplineDocentesGateway';
import type { AgendamentoParticipante, RegistrarPresencaCommand } from '@/domain/model/AgendamentoParticipante';
import type { DocenteAssignment } from '@/domain/model/Docente';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway de participantes de agendamento (acadadmin slice).
 * Root: `/api/v1/agendamentos/{agendamentoId}/participantes`. DELETE 204.
 *
 * @since G.5.17
 */
export class HttpAgendamentoParticipanteGateway implements AgendamentoParticipanteGateway {
  constructor(private readonly client: $Fetch) {}
  private base(agendamentoId: string): string {
    return `/api/v1/agendamentos/${encodeURIComponent(agendamentoId)}/participantes`;
  }

  async listByAgendamento(agendamentoId: string): Promise<AgendamentoParticipante[]> {
    const res = await this.client<ApiResponse<AgendamentoParticipante[]>>(this.base(agendamentoId), { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async adicionar(agendamentoId: string, alunoId: string): Promise<AgendamentoParticipante> {
    const res = await this.client<ApiResponse<AgendamentoParticipante>>(
      this.base(agendamentoId), { method: 'POST', body: { alunoId } });
    assertApiSuccess(res);
    return res.data;
  }

  async confirmar(agendamentoId: string, alunoId: string): Promise<AgendamentoParticipante> {
    const res = await this.client<ApiResponse<AgendamentoParticipante>>(
      `${this.base(agendamentoId)}/${encodeURIComponent(alunoId)}/confirmar`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async cancelar(agendamentoId: string, alunoId: string, motivo: string): Promise<void> {
    // DELETE 204 No Content; motivo vai por query param.
    await this.client(
      `${this.base(agendamentoId)}/${encodeURIComponent(alunoId)}`,
      { method: 'DELETE', params: motivo ? { motivo } : {} });
  }

  async registrarPresenca(
    agendamentoId: string, alunoId: string, cmd: RegistrarPresencaCommand,
  ): Promise<AgendamentoParticipante> {
    const res = await this.client<ApiResponse<AgendamentoParticipante>>(
      `${this.base(agendamentoId)}/${encodeURIComponent(alunoId)}/presenca`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
}

/**
 * HTTP gateway de lookup de docentes por disciplina (faculty slice).
 * `GET /api/v1/disciplines/{disciplineId}/docentes`.
 *
 * @since G.5.17
 */
export class HttpDisciplineDocentesGateway implements DisciplineDocentesGateway {
  constructor(private readonly client: $Fetch) {}

  async listByDiscipline(disciplineId: string): Promise<DocenteAssignment[]> {
    const res = await this.client<ApiResponse<DocenteAssignment[]>>(
      `/api/v1/disciplines/${encodeURIComponent(disciplineId)}/docentes`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
}
